import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import { createStaff, uploadStaffCsv, type UploadStaffResult } from '@/api/staff';

const inputClass =
  'block w-full rounded-[0.5rem] border border-slate-200/80 dark:border-darkmode-400 bg-white dark:bg-darkmode-600 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 shadow-sm focus:border-primary focus:ring-4 focus:ring-primary/20';

const CSV_TEMPLATE = 'Staff Name,Mobile,Email\n';

function downloadCsvTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'ambassador-import-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function Main() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [csvFileName, setCsvFileName] = useState('');
  const [csvError, setCsvError] = useState('');
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState<UploadStaffResult | null>(null);

  const handleIndividualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) {
      setFormError('Staff name is required');
      return;
    }
    if (!trimmedEmail) {
      setFormError('Email is required');
      return;
    }

    setFormSubmitting(true);
    try {
      await createStaff({
        name: trimmedName,
        mobile: mobile.trim() || undefined,
        email: trimmedEmail,
      });
      setFormSuccess('Ambassador added successfully');
      setName('');
      setMobile('');
      setEmail('');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create ambassador');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setCsvResult(null);
    setCsvError('');
    setCsvFileName(file?.name ?? '');
  };

  const handleCsvUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setCsvError('Please choose a CSV file first');
      return;
    }

    setCsvUploading(true);
    setCsvError('');
    setCsvResult(null);

    try {
      const csv = await file.text();
      const result = await uploadStaffCsv(csv);
      setCsvResult(result);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setCsvFileName('');
    } catch (err) {
      setCsvError(err instanceof Error ? err.message : 'CSV upload failed');
    } finally {
      setCsvUploading(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link
              to="/ambassador"
              className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-2"
            >
              <Lucide icon="ArrowLeft" className="w-4 h-4 mr-1.5" />
              Back to Ambassadors
            </Link>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
              Add Ambassador
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Import a CSV file or add one ambassador at a time
            </p>
          </div>
          <Button
            type="button"
            variant="outline-primary"
            className="w-full sm:w-auto"
            onClick={() => navigate('/ambassador')}
          >
            View list
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Lucide icon="Upload" className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              Import CSV
            </h3>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Upload a CSV with columns: <strong>Staff Name</strong> (required),{' '}
            <strong>Email</strong> (required), <strong>Mobile</strong> (optional).
          </p>

          <Button
            type="button"
            variant="outline-primary"
            className="mb-4 w-full sm:w-auto"
            onClick={downloadCsvTemplate}
          >
            <Lucide icon="Download" className="stroke-[1.3] w-4 h-4 mr-2" />
            Download CSV template
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-primary/90"
            onChange={handleCsvFileChange}
          />
          {csvFileName && (
            <p className="mt-2 text-xs text-slate-500">Selected: {csvFileName}</p>
          )}

          {csvError && (
            <div className="mt-4 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
              {csvError}
            </div>
          )}

          {csvResult?.ok && (
            <div className="mt-4 px-4 py-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800">
              <p>
                Imported {csvResult.created ?? 0} ambassador(s). Skipped{' '}
                {csvResult.skipped ?? 0} duplicate name(s).
              </p>
              {(csvResult.errors?.length ?? 0) > 0 && (
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {csvResult.errors!.map((msg) => (
                    <li key={msg}>{msg}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <Button
            type="button"
            variant="primary"
            className="mt-5 w-full sm:w-auto"
            disabled={csvUploading}
            onClick={() => void handleCsvUpload()}
          >
            {csvUploading ? (
              <>
                <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Lucide icon="Upload" className="stroke-[1.3] w-4 h-4 mr-2" />
                Upload CSV
              </>
            )}
          </Button>
        </div>

        <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <Lucide icon="UserPlus" className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              Add individually
            </h3>
          </div>

          <form onSubmit={(e) => void handleIndividualSubmit(e)} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Staff name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Mobile
              </label>
              <input
                type="text"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className={inputClass}
              />
            </div>

            {formError && (
              <div className="px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
                {formError}
              </div>
            )}

            {formSuccess && (
              <div className="px-4 py-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800">
                {formSuccess}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full sm:w-auto"
              disabled={formSubmitting}
            >
              {formSubmitting ? (
                <>
                  <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Lucide icon="Plus" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Add Ambassador
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Main;
